import { useRef, useState } from 'react'
import { useSearchParams } from 'react-router'

import { EMPTY_OBJECT } from '../../../constants/common'
import { ScreenContainerRef } from '../../../context'
import { getSubSearchParam } from '../../../utils/route'
import { EntityPageProfileLayout } from '../../Page'
import { EmailProfileActionsContextProvider } from '../EmailProfileActionsContextProvider'
import { EmailProfileContextProvider } from '../EmailProfileContextProvider'
import { EmailProfileHeader } from '../EmailProfileHeader'
import { EmailProfileMessagesList } from '../EmailProfileMessagesList'
import { EmailProfileMessagesListExpandContextProvider } from '../EmailProfileMessagesListExpandContextProvider'
import {
  EmailProfileActionsWidget,
  EmailProfileWidgetList,
} from '../EmailProfileWidget'

import { QA_NAME_EMAIL_PROFILE_PAGE } from './EmailProfile.constants'
import { useEmailProfileStickyHeaderHeight } from './EmailProfile.hooks'
import { isEmailProfileUnavailable } from './EmailProfile.utils'
import { EmailProfileLoader } from './EmailProfileLoader'
import { EmailProfileUnavailable } from './EmailProfileUnavailable'

import * as styles from './EmailProfile.module.css'

function EmailProfile({
  actionsContextProps,
  className = styles.EmailProfile,
  entityData,
  headerProps,
  isInitiated,
  isLoading,
  layoutProps,
  loadWidgetsData,
  mailUuid,
  messages,
  messagesListProps,
  navigation = null,
  serverError,
  summaryProps = EMPTY_OBJECT,
  widgetsData,
  widgetsProps: { actionsProps } = EMPTY_OBJECT,
  withWidgets,
  onArchiveEmail,
  onReloadEntity,
  onReloadMessages,
  onUpdateEntity,
  onUpdateMessage,
}) {
  const [searchParams] = useSearchParams()
  const sub = getSubSearchParam(searchParams)

  const [modal, setModal] = useState(null)
  const [isPerformingAction, setPerformingAction] = useState(false)

  const pageScrollWrapperRef = useRef(null)

  const { handleStickyHeaderRef } = useEmailProfileStickyHeaderHeight({
    scrollWrapperRef: pageScrollWrapperRef,
  })

  const isSkeleton = !isInitiated || !widgetsData

  function handleCloseModal() {
    setModal(null)
  }

  return isEmailProfileUnavailable({
    isNavigationEnabled: navigation?.isEnabled,
    serverError,
  }) ? (
    <EmailProfileUnavailable navigation={navigation} />
  ) : (
    <EmailProfileContextProvider
      entityData={entityData}
      handleArchiveEmail={onArchiveEmail}
      handleCloseModal={handleCloseModal}
      handleReloadEntity={onReloadEntity}
      handleReloadMessages={onReloadMessages}
      handleUpdateEntity={onUpdateEntity}
      handleUpdateMessage={onUpdateMessage}
      isInitiated={isInitiated}
      isLoading={isLoading}
      isPerformingAction={isPerformingAction}
      loadWidgetsData={loadWidgetsData}
      mailUuid={mailUuid}
      messages={messages}
      navigation={navigation}
      setModal={setModal}
      setPerformingAction={setPerformingAction}
      sub={sub}
      summaryProps={summaryProps}
      widgetsData={widgetsData}
    >
      <EmailProfileActionsContextProvider {...actionsContextProps}>
        <EmailProfileMessagesListExpandContextProvider messages={messages}>
          <ScreenContainerRef value={pageScrollWrapperRef}>
            <EntityPageProfileLayout
              {...layoutProps}
              Header={EmailProfileHeader}
              Loader={EmailProfileLoader}
              actionsWidget={
                withWidgets ? (
                  <EmailProfileActionsWidget
                    {...actionsProps}
                    className={styles.actionsWidget}
                    isSkeleton={isSkeleton}
                  />
                ) : null
              }
              className={className}
              headerProps={{
                ...headerProps,
                stickyTitleRef: handleStickyHeaderRef,
              }}
              initialWidgetsHidden={navigation?.initialWidgetsHidden}
              qaName={QA_NAME_EMAIL_PROFILE_PAGE}
              scrollWrapperRef={pageScrollWrapperRef}
              serverError={serverError}
              widgets={
                withWidgets ? (
                  <EmailProfileWidgetList
                    foldersWidgetClassName={styles.foldersWidget}
                    isSkeleton={isSkeleton}
                    widgetClassName={styles.widget}
                  />
                ) : null
              }
            >
              <EmailProfileMessagesList
                {...messagesListProps}
                items={messages}
              />
            </EntityPageProfileLayout>
          </ScreenContainerRef>
          {modal}
        </EmailProfileMessagesListExpandContextProvider>
      </EmailProfileActionsContextProvider>
    </EmailProfileContextProvider>
  )
}

export default EmailProfile
